import moment from 'moment-timezone';
import map from 'lodash/map';
import { humanize } from '@utils';
import alphanumericSort from '@utils/alphanumericSort';
import { derivedDimensionsMetaStore } from '@globalStore/dimensionsStore';
import { DIMENSION_TYPES_CONSTANTS } from '@utils/metrics';
import { formatTime } from '@components/metrics-panel-hz/utils/format';

const metricTypes = {
  DIMENSIONS: 'dimensions',
  METRICS: 'metrics',
};

export const customerOverrideForDrilldown = {
  1960180521: 'user_email',
  1928809131: 'user_email', //c3.dryrun account on qe2
};

const getStaticDimensions = (customerId) => {
  return ['client_id', customerOverrideForDrilldown[customerId] || 'app_user_id', 'device_name'];
};

export const getMandatoryDimensions = (customerId, selectedMetric) => {
  const staticDimensions = getStaticDimensions(customerId);
  return [...staticDimensions, selectedMetric];
};

function replaceObjectKeys(obj, keyMap) {
  const newObj = { ...obj };
  for (const oldKey in keyMap) {
    if (oldKey in newObj) {
      const newKey = keyMap[oldKey];
      newObj[newKey] = newObj[oldKey];
      delete newObj[oldKey];
    }
  }
  return newObj;
}

function reorderObjectKeys(obj, keyOrder) {
  const reorderedObject = {
    Mandatory: [],
  };
  for (const key of keyOrder) {
    if (key in obj) {
      reorderedObject[key] = obj[key];
    }
  }
  return reorderedObject;
}

function modifyArrayOfObjectsForKey(obj, metricType) {
  for (const key in obj) {
    obj[key] = obj[key].map((item) => {
      return { ...item, metric: item.key, type: metricType };
    });
  }
  return obj;
}

export const getDimensionData = ({
  dimensionsCategories,
  currentDimensionsCategoryOrder,
  dimensionsListCategory,
}) => {
  const reorderedCategories = reorderObjectKeys(
    dimensionsCategories,
    currentDimensionsCategoryOrder,
  );
  const replacedCategories = replaceObjectKeys(reorderedCategories, dimensionsListCategory);
  const dimensionData = modifyArrayOfObjectsForKey(replacedCategories, metricTypes.DIMENSIONS);
  return dimensionData;
};

export const getMetricsData = (metricsCategories) => {
  const metricsData = modifyArrayOfObjectsForKey(metricsCategories, metricTypes.METRICS);
  return metricsData;
};

export const getExtractedMandatory = (categoriesData, mandatoryDimensions) => {
  const mandatoryCategories: any = [];
  for (const key in categoriesData) {
    categoriesData[key] = categoriesData[key].filter((item) => {
      if (!mandatoryDimensions.includes(item.key)) {
        return item;
      } else {
        mandatoryCategories.push(item);
      }
    });
  }
  categoriesData['Mandatory'] = mandatoryCategories;
  return categoriesData;
};

export const getOrderedMetrics = (metricList, dimensionList, customerId) => {
  const staticDimensions = getStaticDimensions(customerId);
  const dimensionKeys = map(dimensionList, 'field');
  const metricKeys = Object.keys(metricList);
  const orderedMetrics: any = [...staticDimensions, ...dimensionKeys, ...metricKeys];
  return orderedMetrics;
};

export const getSegregatedMetrics = (selectedMetrics, metricList) => {
  const segregatedMetrics: any = {
    dimensions: [],
    metrics: [],
  };
  for (const metric of selectedMetrics) {
    if (metricList[metric]) {
      if (metricList[metric].type === metricTypes.DIMENSIONS) {
        segregatedMetrics.dimensions.push(metric);
      } else {
        segregatedMetrics.metrics.push(metric);
      }
    }
  }
  return segregatedMetrics;
};

const formatStyle = 'HHH DD, HH:MM';

export const humanizeHoc = (value, unit) => {
  if (value === null || value === undefined || value === 'NA') return value;
  switch (unit) {
    case 'Kbps':
      return humanize(value * 1000, 'bps') + 'bps';
    case 'min':
    case 'mins':
      return formatTime(value * 60 * 1000);
    case 'hour':
      return formatTime(value * 60 * 60 * 1000);
    case 'sec':
      return formatTime(value * 1000);
    case 'ms':
      return formatTime(value);
    case 'fps':
    case '%':
      return `${Number(value.toFixed(2))}${unit}`;
    case 'unitless':
    case 'ratio':
      if (isNaN(value)) {
        return value;
      } else {
        return `${Number(Number(value).toFixed(2))}`;
      }
  }
};

export const momentTime = (value) => moment(value).format(formatStyle);

export const getSortAndSearchTable = (data, searchData, sortData, selectedMetrics) => {
  const filledData = data.map((item) => {
    const newItem = { ...item };
    for (const metric of selectedMetrics) {
      if (newItem[metric] === undefined) {
        newItem[metric] = 'NA';
      }
    }
    return newItem;
  });
  let finalData = filledData;
  const { text, key: searchKey } = searchData;
  const { order, key: sortKey } = sortData;
  const re = new RegExp(text, 'i');
  if (searchKey && selectedMetrics.includes(searchKey)) {
    const keyFixed = searchKey.replace(/\$/g, '.');
    finalData = finalData.filter((item) => item[keyFixed] && re.test(item[keyFixed]));
  }
  if (sortKey && selectedMetrics.includes(sortKey)) {
    const result: any = {
      remainingObjects: [],
      removedObjects: [],
    };
    finalData.forEach((obj) => {
      if (obj[sortKey] === 'NA') {
        result.removedObjects.push(obj);
      } else {
        result.remainingObjects.push(obj);
      }
    });
    const sortedData = result.remainingObjects.sort((prev, next) =>
      alphanumericSort(order, prev[sortKey], next[sortKey]) ? 1 : -1,
    );
    finalData = [...sortedData, ...result.removedObjects];
  }
  return finalData;
};

export const filteredDimensions = (dimensions, filterMode) => {
  let returnDimensions = dimensions;
  const { dimensionByTypes } = derivedDimensionsMetaStore.getState();
  if (
    filterMode === DIMENSION_TYPES_CONSTANTS.immutableGlobalMetadata ||
    filterMode === DIMENSION_TYPES_CONSTANTS.mutableGlobalMetadata
  ) {
    returnDimensions = dimensions.filter(
      (dimension) =>
        dimensionByTypes[dimension] !== DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata,
    );
  }
  return returnDimensions;
};

export const MAX_ANCHORED_COUNT = 3;
